import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Card, CardContent, CardMedia, Grid, Chip, Accordion, AccordionSummary, AccordionDetails, CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState, useEffect } from 'react';

export interface SharedFile {
    path: string;
    thumbnail: string;
    title: string;
    description: string;
    license?: string;
    url?: string;
    source?: string;
    author?: string;
    tags?: string[];
}

export type SharedFileList = { [key: string]: SharedFile[] };

type SharedFileBrowserProps = {
    title: string;
    sharedFiles: SharedFileList;
    show: boolean;
    onClose: () => void;
    onSelectFile: (file: SharedFile) => void;
    isLoading?: boolean;
    embedded?: boolean;
}

const SharedFileBrowser = ({ title, sharedFiles, show, onClose, onSelectFile, isLoading = false, embedded = false }: SharedFileBrowserProps) => {
    const [selectedFile, setSelectedFile] = useState<SharedFile | null>(null);

    useEffect(() => {
        if (!show) {
            setSelectedFile(null);
        }
    }, [show]);

    const handleSelect = (file: SharedFile) => {
        setSelectedFile(file);
        if (embedded) {
            onSelectFile(file);
        }
    };

    const handleConfirm = () => {
        if (selectedFile) {
            onSelectFile(selectedFile);
        }
    };

    const content = (
        <>
            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : (
                <Box>
                    {Object.entries(sharedFiles).map(([section, files]) => (
                        <Accordion key={section} defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6">{section}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    {files.map((file) => (
                                        <Grid item xs={12} sm={6} md={4} key={file.path}>
                                            <Card
                                                sx={{
                                                    cursor: 'pointer',
                                                    border: selectedFile?.path === file.path ? '2px solid #1976d2' : '1px solid transparent',
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                }}
                                                onClick={() => handleSelect(file)}
                                            >
                                                {file.thumbnail && (
                                                    <CardMedia
                                                        component="img"
                                                        height="140"
                                                        image={file.thumbnail}
                                                        alt={file.title}
                                                    />
                                                )}
                                                <CardContent sx={{ flexGrow: 1 }}>
                                                    <Typography gutterBottom variant="h6" component="div">
                                                        {file.title}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                        {file.description}
                                                    </Typography>
                                                    {file.author && (
                                                        <Typography variant="caption" display="block">
                                                            Author: {file.author}
                                                        </Typography>
                                                    )}
                                                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {file.tags?.map((tag) => (
                                                            <Chip key={tag} label={tag} size="small" />
                                                        ))}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    {Object.keys(sharedFiles).length === 0 && (
                        <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                            No shared files available.
                        </Typography>
                    )}
                </Box>
            )}
        </>
    );

    if (embedded) {
        return content;
    }

    return (
        <Dialog open={show} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                {content}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleConfirm} disabled={!selectedFile} variant="contained">
                    Select
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SharedFileBrowser;
